import { supabase } from './supabaseClient';
import { mergeStreak } from './guestProgress';
import { lessonProgressService } from './lessonProgressService';
import { adoptGuestPractice, loadPracticeSnapshot, setStudyStreak } from './practiceStore';
import { claimStorySaves } from './reviewDeck';
import { readStudyStats, writeStudyStats } from './studyStatsStore';
import {
  ONBOARDING_STORAGE_KEY,
  emptyRecord,
  startProClock,
  guestLessonAllowed,
  guestPathAllowed,
  guestPracticeAllowed,
  guestStarterAllowed,
  inviteCodeFor,
  isEnglishLevel,
  referralEvent,
  type OnboardingRecord,
} from './onboardingModel';

function newGuestKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `guest_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function missingRemote(message: string): boolean {
  return /schema cache|does not exist|could not find|primary_journey|onboarding_completed_at|onboarding_events|claim_onboarding|daily_minutes|notification_choice|time_of_day|invite_choice|trial_choice|pro_until|english_level|referral_invites|redeem_invite|premium_tier|premium_access_until|claim_guest_referrals/i.test(message);
}

export function loadRecord(): OnboardingRecord | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardingRecord;
    if (!parsed || parsed.version !== 1 || typeof parsed.guestKey !== 'string' || !parsed.guestKey) return null;
    const profileName = typeof parsed.profileName === 'string' ? parsed.profileName.trim().slice(0, 80) : null;
    const profileEmail = typeof parsed.profileEmail === 'string' ? parsed.profileEmail.trim().slice(0, 120) : null;
    const rawPoints = parsed.lessonPoints;
    const lessonPoints = typeof rawPoints === 'number' && Number.isFinite(rawPoints) ? Math.max(0, Math.floor(rawPoints)) : null;
    return {
      ...emptyRecord(parsed.guestKey),
      ...parsed,
      proReminderSeen: Boolean(parsed.proReminderSeen),
      englishLevel: isEnglishLevel(Number(parsed.englishLevel)) ? Number(parsed.englishLevel) as OnboardingRecord['englishLevel'] : null,
      profileName: profileName || null,
      profileEmail: profileEmail || null,
      planChoice: parsed.planChoice === 'free' || parsed.planChoice === 'pro' ? parsed.planChoice : null,
      lessonPoints,
    };
  } catch {
    return null;
  }
}

export function saveRecord(record: OnboardingRecord): void {
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(record));
}

export function loadOrCreate(): OnboardingRecord {
  const existing = loadRecord();
  if (existing) return existing;
  const created = emptyRecord(newGuestKey());
  saveRecord(created);
  return created;
}

export function guestMayOpenLesson(slug: string, lessonSlug: string): boolean {
  return guestLessonAllowed(loadRecord(), slug, lessonSlug);
}

export function guestMayOpenPath(slug: string): boolean {
  return guestPathAllowed(loadRecord(), slug);
}

export function guestMayPracticeTopic(slug: string, mode: string | undefined): boolean {
  return guestPracticeAllowed(loadRecord(), slug, mode);
}

export async function publishReferral(record: OnboardingRecord): Promise<'saved' | 'local'> {
  const event = referralEvent(record);
  if (!event) return 'local';
  const base = {
    guest_key: event.guest_key,
    user_id: null,
    need: event.need,
    journey_slug: event.journey_slug,
    referral_source: event.referral_source,
    driving_goal: event.driving_goal,
    driving_state: event.driving_state,
    test_date: event.test_date,
  };
  const full = {
    ...base,
    daily_minutes: record.dailyMinutes,
    notification_choice: record.notificationChoice,
    time_of_day: record.timeOfDay,
    invite_choice: record.inviteChoice,
    trial_choice: record.trialChoice,
    pro_until: record.proUntil,
    english_level: record.englishLevel,
  };
  const first = await supabase.from('onboarding_events').insert(full);
  if (!first.error) return 'saved';
  if (!missingRemote(first.error.message)) {
    console.warn('Referral stayed on this device.', first.error.message);
    return 'local';
  }
  const second = await supabase.from('onboarding_events').insert(base);
  if (second.error) {
    console.warn('Referral stayed on this device.', second.error.message);
    return 'local';
  }
  return 'saved';
}

export async function publishInvite(record: OnboardingRecord): Promise<OnboardingRecord> {
  const inviteCode = record.inviteCode || inviteCodeFor(record.guestKey);
  const latest = loadRecord();
  const next = latest && latest.guestKey === record.guestKey ? { ...latest, inviteCode } : { ...record, inviteCode };
  if (record.inviteChoice && !next.inviteChoice) next.inviteChoice = record.inviteChoice;
  saveRecord(next);
  const { error } = await supabase.from('referral_invites').insert({
    invite_code: inviteCode,
    referrer_guest_key: record.guestKey,
    referrer_user_id: null,
    pass_days: 7,
  });
  if (error && !/duplicate|already exists|23505/i.test(error.message)) {
    console.warn('Invite link stayed on this device.', error.message);
  }
  return next;
}

/** Confirm a friend's code. A missing table keeps a local 7-day pass. An explicit no does not. */
export async function redeemInvite(record: OnboardingRecord): Promise<OnboardingRecord> {
  if (!record.incomingInvite || record.incomingInvite === inviteCodeFor(record.guestKey) || record.incomingInvite === record.inviteCode) {
    return record;
  }
  if (record.trialChoice === 'invite' && record.proUntil) return record;
  const { data, error } = await supabase.rpc('redeem_invite', {
    p_code: record.incomingInvite,
    p_guest_key: record.guestKey,
  });
  if (error) {
    if (!missingRemote(error.message)) {
      console.warn('Invite was not confirmed.', error.message);
      return record;
    }
    const next = startProClock(record);
    saveRecord(next);
    return next;
  }
  if (Number(data) !== 7) return record;
  const next = startProClock(record);
  saveRecord(next);
  return next;
}

export function markLessonFinished(): OnboardingRecord | null {
  const record = loadRecord();
  if (!record) return null;
  const next = { ...record, lessonFinishedAt: new Date().toISOString() };
  saveRecord(next);
  return next;
}

async function mergeGuestProgress(authUserId: string, journeySlug: string): Promise<void> {
  const localIds = [...lessonProgressService.getCompletedLessonsFromLocal()];
  for (const lessonId of localIds) {
    const { error } = await supabase.from('user_lesson_progress').upsert({
      user_id: authUserId,
      lesson_id: lessonId,
      journey_slug: journeySlug,
      completed_at: new Date().toISOString(),
    }, { onConflict: 'user_id,lesson_id' });
    if (error) {
      console.warn('Could not merge a local lesson.', error.message);
      break;
    }
  }

  const anonId = localStorage.getItem('anonymous_user_id');
  if (!anonId || anonId === authUserId) return;
  const { data, error } = await supabase
    .from('user_lesson_progress')
    .select('lesson_id, journey_slug, completed_at')
    .eq('user_id', anonId);
  if (error || !data) return;
  for (const row of data) {
    await supabase.from('user_lesson_progress').upsert({
      user_id: authUserId,
      lesson_id: row.lesson_id,
      journey_slug: row.journey_slug || journeySlug,
      completed_at: row.completed_at,
    }, { onConflict: 'user_id,lesson_id' });
  }
}

/** Link the guest referral row and local lesson progress to the signed-in user. */
export async function settleOnboarding(user: { id: string }): Promise<boolean> {
  const record = loadRecord();
  if (!record?.lessonFinishedAt || !record.journeySlug || !record.guestKey || !record.need) return false;
  if (record.claimedAt === user.id) return true;

  const { error: metaError } = await supabase.auth.updateUser({
    data: {
      profile_complete: true,
      primary_journey: record.journeySlug,
      ...(record.profileName ? { full_name: record.profileName } : {}),
      ...(record.profileEmail ? { contact_email: record.profileEmail } : {}),
    },
  });
  if (metaError && !missingRemote(metaError.message)) {
    console.warn('Could not save the journey on the account.', metaError.message);
  }

  const claim = await supabase.rpc('claim_onboarding', { p_guest_key: record.guestKey });
  const referralClaim = await supabase.rpc('claim_guest_referrals', { p_guest_key: record.guestKey });
  if (referralClaim.error && !missingRemote(referralClaim.error.message)) {
    console.warn('Could not attach the invite.', referralClaim.error.message);
  }
  const profile = await supabase
    .from('profiles')
    .update({
      primary_journey: record.journeySlug,
      onboarding_completed_at: record.lessonFinishedAt,
      ...(record.profileName ? { first_name: record.profileName } : {}),
    })
    .eq('id', user.id);
  if (profile.error && !missingRemote(profile.error.message)) {
    console.warn('Could not write the primary journey.', profile.error.message);
  }
  if (record.proUntil) {
    const existing = await supabase
      .from('profiles')
      .select('subscription_status, premium_tier')
      .eq('id', user.id)
      .maybeSingle();
    const paid = existing.data?.subscription_status === 'active' && existing.data?.premium_tier && existing.data.premium_tier !== 'guest_pass';
    if (!paid) {
      const pass = await supabase
        .from('profiles')
        .update({ premium_tier: 'guest_pass', premium_access_until: record.proUntil })
        .eq('id', user.id);
      if (pass.error && !missingRemote(pass.error.message)) {
        console.warn('Could not save the Pro pass.', pass.error.message);
      }
    }
  }

  await mergeGuestProgress(user.id, record.journeySlug);
  await adoptGuestPractice(user.id);
  claimStorySaves(user.id);
  const stats = readStudyStats();
  const practice = await loadPracticeSnapshot(user.id);
  const streak = mergeStreak(
    { current: stats.current, longest: stats.longest, lastDate: stats.lastDate },
    practice.streak,
  );
  await setStudyStreak(user.id, streak);
  if (streak.current !== stats.current || streak.longest !== stats.longest || streak.lastDate !== stats.lastDate) {
    writeStudyStats({
      ...stats,
      current: streak.current,
      longest: Math.max(stats.longest, streak.longest),
      lastDate: streak.lastDate,
    });
  }

  const claimed = !claim.error;
  saveRecord({ ...record, claimedAt: claimed ? user.id : record.claimedAt });
  return true;
}

export async function completeLessonAndLeave(options: {
  userId: string | null;
  lessonId: string;
  journeySlug: string;
  lessonSlug: string;
  navigate: (to: string) => void;
  correctBlanks?: number;
  blankCount?: number;
  skipped?: boolean;
}): Promise<void> {
  const correct = Math.max(0, Math.floor(options.correctBlanks ?? 0));
  const blanks = Math.max(correct, Math.floor(options.blankCount ?? correct));
  const params = new URLSearchParams({
    path: options.journeySlug,
    lesson: options.lessonSlug,
    id: options.lessonId,
    correct: String(options.skipped ? 0 : correct),
    blanks: String(blanks),
    skipped: options.skipped ? '1' : '0',
  });
  options.navigate(`/lesson-done?${params.toString()}`);
}

/** Guest first lessons continue to the account step. Everyone else returns to the course. */
export function lessonFinishExit(userId: string | null, journeySlug: string, lessonSlug: string): string {
  const starter = lessonSlug === 'starter' && guestStarterAllowed(loadRecord(), journeySlug);
  if (!userId && (guestMayOpenLesson(journeySlug, lessonSlug) || starter)) {
    markLessonFinished();
    return '/start/account';
  }
  return `/courses/${journeySlug}`;
}
